(function () {
	'use strict';

	const tagName = 'syk-count-up';
	const pluginPromises = new Map();
	const symbolClasses = {
		'+': 'has-plus',
		'!': 'has-exc',
		'$': 'has-usd',
		'€': 'has-eur',
		'£': 'has-gbp',
		'¥': 'has-jpy',
		'₹': 'has-inr',
		'%': 'has-per',
		'★': 'has-str'
	};
	const trailingSymbolPattern = /[+!$€£¥₹%★]$/;

	if (!('customElements' in window) || window.customElements.get(tagName)) {
		return;
	}

	function normalizeUrl(value) {
		if (typeof value !== 'string' || value.trim() === '') {
			return null;
		}

		try {
			return new URL(value, document.baseURI).href;
		} catch (error) {
			return null;
		}
	}

	function findScript(url) {
		for (const script of document.scripts) {
			if (normalizeUrl(script.getAttribute('src')) === url) {
				return script;
			}
		}

		return null;
	}

	function loadPlugin(value) {
		if (typeof window.CountUp === 'function') {
			return Promise.resolve(window.CountUp);
		}

		const url = normalizeUrl(value);

		if (!url) {
			return Promise.reject(new Error('Invalid CountUp plugin URL.'));
		}

		if (pluginPromises.has(url)) {
			return pluginPromises.get(url);
		}

		const promise = new Promise((resolve, reject) => {
			let script = findScript(url);

			if (
				script &&
				script.hasAttribute('data-syk-count-up-plugin') &&
				script.getAttribute('data-syk-count-up-state') === 'error'
			) {
				script.remove();
				script = null;
			}

			const existingScript = Boolean(script);
			let settled = false;

			if (!script) {
				script = document.createElement('script');
				script.src = url;
				script.async = true;
				script.setAttribute('data-syk-count-up-plugin', '');
			}

			const cleanup = function () {
				script.removeEventListener('load', handleLoad);
				script.removeEventListener('error', handleError);
			};

			const handleLoad = function () {
				if (settled) {
					return;
				}

				settled = true;
				cleanup();

				if (typeof window.CountUp === 'function') {
					script.setAttribute('data-syk-count-up-state', 'loaded');
					resolve(window.CountUp);
					return;
				}

				script.setAttribute('data-syk-count-up-state', 'error');
				reject(new Error('CountUp plugin loaded without exposing CountUp.'));
			};

			const handleError = function () {
				if (settled) {
					return;
				}

				settled = true;
				cleanup();
				script.setAttribute('data-syk-count-up-state', 'error');
				reject(new Error('CountUp plugin failed to load.'));
			};

			script.addEventListener('load', handleLoad);
			script.addEventListener('error', handleError);

			if (existingScript) {
				const state = script.getAttribute('data-syk-count-up-state');

				if (state === 'loaded' || script.readyState === 'complete' || script.readyState === 'loaded') {
					window.setTimeout(handleLoad, 0);
				} else if (state === 'error') {
					window.setTimeout(handleError, 0);
				}
			} else {
				document.head.appendChild(script);
			}
		});

		pluginPromises.set(url, promise);
		promise.then(null, function () {
			if (pluginPromises.get(url) === promise) {
				pluginPromises.delete(url);
			}
		});

		return promise;
	}

	class SykCountUp extends HTMLElement {
		constructor() {
			super();
			this.connectionId = 0;
			this.observer = null;
			this.entries = [];
			this.counts = [];
			this.countUpConstructor = null;
			this.pluginReady = false;
			this.intersected = false;
			this.starting = false;
			this.started = false;
		}

		connectedCallback() {
			this.connectionId += 1;
			this.disconnectObserver();

			if (this.started) {
				this.settleCounts();
				return;
			}

			const pluginSrc = this.getAttribute('data-plugin-src');
			const list = this.querySelector('.l4cu');

			if (!normalizeUrl(pluginSrc) || !list || list.closest(tagName) !== this) {
				return;
			}

			const entries = this.collectEntries(list);

			if (!entries.length) {
				return;
			}

			const connectionId = this.connectionId;
			this.entries = entries;
			this.countUpConstructor = null;
			this.pluginReady = false;
			this.intersected = false;

			loadPlugin(pluginSrc).then((CountUpConstructor) => {
				if (!this.isCurrentConnection(connectionId)) {
					return;
				}

				this.countUpConstructor = CountUpConstructor;
				this.pluginReady = true;
				this.maybeStart(connectionId);
			}, () => {
				if (this.isCurrentConnection(connectionId)) {
					this.disconnectObserver();
				}

				// The server-rendered value remains the fallback when the plugin fails.
			});

			if (typeof window.IntersectionObserver === 'function') {
				this.observer = new IntersectionObserver((observerEntries) => {
					if (!this.isCurrentConnection(connectionId)) {
						return;
					}

					for (const observerEntry of observerEntries) {
						if (!observerEntry.isIntersecting) {
							continue;
						}

						this.intersected = true;
						this.disconnectObserver();
						this.maybeStart(connectionId);
						break;
					}
				});
				this.observer.observe(list);
			} else {
				this.intersected = true;
				this.maybeStart(connectionId);
			}
		}

		disconnectedCallback() {
			this.connectionId += 1;
			this.disconnectObserver();

			if (this.started) {
				this.settleCounts();
			}
		}

		isCurrentConnection(connectionId) {
			return this.isConnected && connectionId === this.connectionId;
		}

		collectEntries(list) {
			const entries = [];

			for (const item of list.children) {
				if (item.tagName.toLowerCase() !== 'li') {
					continue;
				}

				let element = null;

				for (const child of item.children) {
					if (child.tagName.toLowerCase() === 'span') {
						element = child;
						break;
					}
				}

				if (!element || !element.hasAttribute('data-count-up-value') || element.closest(tagName) !== this) {
					continue;
				}

				const parsed = this.parseValue(element.getAttribute('data-count-up-value'));

				if (!parsed) {
					continue;
				}

				entries.push({
					element: element,
					value: parsed.value,
					decimalPlaces: parsed.decimalPlaces,
					classes: parsed.classes,
					after: parsed.after,
					raw: parsed.raw
				});
			}

			return entries;
		}

		parseValue(value) {
			if (typeof value !== 'string') {
				return null;
			}

			const raw = value.trim();

			if (raw === '') {
				return null;
			}

			let numericToken = raw;
			const classes = [];

			for (const symbol in symbolClasses) {
				if (numericToken.indexOf(symbol) === -1) {
					continue;
				}

				numericToken = numericToken.split(symbol).join('');
				classes.push(symbolClasses[symbol]);
			}

			if (!/^-?\d+(?:\.\d+)?$/.test(numericToken)) {
				return null;
			}

			const number = Number(numericToken);

			if (!Number.isFinite(number)) {
				return null;
			}

			const decimalIndex = numericToken.indexOf('.');
			const decimalPlaces = decimalIndex === -1 ? 0 : numericToken.length - decimalIndex - 1;

			return {
				raw: raw,
				value: number,
				decimalPlaces: decimalPlaces,
				classes: classes,
				after: trailingSymbolPattern.test(raw)
			};
		}

		maybeStart(connectionId) {
			if (
				!this.isCurrentConnection(connectionId) ||
				!this.pluginReady ||
				!this.intersected ||
				this.starting ||
				this.started
			) {
				return;
			}

			this.starting = true;
			const reduceMotion = typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
			const counts = [];

			try {
				for (const entry of this.entries) {
					const count = this.enhanceEntry(entry, reduceMotion);

					if (count) {
						counts.push(count);
					}
				}
			} finally {
				this.counts = counts;
				this.started = counts.length > 0;
				this.starting = false;
			}
		}

		enhanceEntry(entry, reduceMotion) {
			const element = entry.element;

			if (!element.isConnected || element.closest(tagName) !== this) {
				return null;
			}

			const main = document.createElement('span');
			const clone = document.createElement('span');
			const hadContClass = element.classList.contains('cont');

			main.className = 'main';
			clone.className = 'clone';
			clone.setAttribute('aria-hidden', 'true');
			clone.setAttribute('data-val', entry.raw);

			for (const className of entry.classes) {
				clone.classList.add(className);
			}

			if (entry.after) {
				clone.classList.add('after');
			}

			while (element.firstChild) {
				main.appendChild(element.firstChild);
			}

			element.appendChild(main);
			element.appendChild(clone);
			element.classList.add('cont');

			let count;

			try {
				count = new this.countUpConstructor(clone, entry.value, {
					decimalPlaces: entry.decimalPlaces,
					duration: reduceMotion ? 0 : 3
				});
			} catch (error) {
				this.restoreEntry(element, main, clone, hadContClass);
				return null;
			}

			if (count.error) {
				this.restoreEntry(element, main, clone, hadContClass);
				return null;
			}

			try {
				if (reduceMotion) {
					count.frameVal = count.endVal;
					count.paused = true;
					count.printValue(count.endVal);
				} else {
					count.start();
				}
			} catch (error) {
				if (count.rAF !== undefined && count.rAF !== null) {
					window.cancelAnimationFrame(count.rAF);
					count.rAF = null;
				}

				this.restoreEntry(element, main, clone, hadContClass);
				return null;
			}

			return count;
		}

		restoreEntry(element, main, clone, hadContClass) {
			while (main.firstChild) {
				element.insertBefore(main.firstChild, main);
			}

			main.remove();
			clone.remove();

			if (!hadContClass) {
				element.classList.remove('cont');
			}
		}

		settleCounts() {
			for (const count of this.counts) {
				try {
					if (count.rAF !== undefined && count.rAF !== null) {
						window.cancelAnimationFrame(count.rAF);
						count.rAF = null;
					}

					count.paused = true;
					count.startTime = null;
					count.frameVal = count.endVal;

					if (typeof count.printValue === 'function') {
						count.printValue(count.endVal);
					}
				} catch (error) {
					// Disconnection must not throw or remove the static fallback.
				}
			}
		}

		disconnectObserver() {
			if (this.observer) {
				this.observer.disconnect();
				this.observer = null;
			}
		}
	}

	window.customElements.define(tagName, SykCountUp);
})();
